// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Intake service: the seam between the UI/controller and query-health-api. It starts the
// mode-agnostic conversation, polls status, and fetches the prep note. This is a THIN
// client — it invents nothing. The backend mints the ephemeral Vapi webToken and owns the
// assistant (prompt, model, custom-llm→ai-gateway wiring, voice, transcriber); the browser
// receives only { webToken, assistantId, assistantOverrides } + opaque ids and carries no
// PHI and no Vapi keys. iOS/Android call these same endpoints.

import { request, postJson } from '@/lib/api'
import { setConversationId } from '@/lib/session'
import { formatTime } from './util'
import type {
  IntakeLocale,
  IntakeMessage,
  IntakeStatusResponse,
  KioskRedeemResponse,
  MessagesResponse,
  PrepNote,
  PrepNoteSubmit,
  StartIntakeResponse,
} from './types'

/**
 * Fetch the CANONICAL transcript (GET /v1/intake/{id}/messages) — the persisted
 * per-turn record for both text and voice, in sequence order. This is the source
 * of truth for rendering/repainting (NOT Vapi's live STT). Ids are derived from
 * sequenceNumber so repeated fetches produce stable React keys (no re-mount flicker).
 */
export async function getMessages(conversationId: string): Promise<IntakeMessage[]> {
  const res = await request<MessagesResponse>(`/v1/intake/${conversationId}/messages`)
  // Never render an empty/whitespace turn (e.g. Vapi's assistant-first trigger surfaces
  // as a blank "You" bubble). The backend also skips persisting these; guard here too.
  return res.messages
    .filter((m) => m.content.trim() !== '')
    .map((m) => ({
    id: `seq-${m.sequenceNumber}`,
    role: m.role,
    content: m.content,
    heading: m.heading,
    modality: m.modality,
    sequenceNumber: m.sequenceNumber,
    emergencyFlagged: m.emergencyFlagged,
    timestamp: formatTime(new Date(m.createdAt)),
  }))
}

/**
 * Redeem an in-clinic kiosk lock token (minted by the provider via POST
 * /v1/kiosk/start) for a patient session. The lock token IS the authorization —
 * it rides in the body, never the URL — and the flow is provider_authorized, so
 * there is no DOB/OTP step. Returns a short-lived session JWT + first name + locale.
 */
export function redeemKiosk(lockToken: string): Promise<KioskRedeemResponse> {
  return postJson<KioskRedeemResponse>('/v1/kiosk/redeem', { lockToken })
}

/** Start (or resume) the patient's intake conversation and get voice credentials. */
export async function startIntake(locale: IntakeLocale): Promise<StartIntakeResponse> {
  const res = await postJson<StartIntakeResponse>('/v1/intake/start', { locale })
  setConversationId(res.conversationId)
  return res
}

/** Poll conversation status — backend is authoritative for emergency/complete. */
export function getIntakeStatus(conversationId: string): Promise<IntakeStatusResponse> {
  return request<IntakeStatusResponse>(`/v1/intake/${conversationId}`)
}

/**
 * Fetch the generated visit prep note. Generation is async: poll getIntakeStatus for
 * `prepNoteReady` (~2–3s), then call this — it 404s until the note exists.
 */
export function getPrepNote(conversationId: string): Promise<PrepNote> {
  return request<PrepNote>(`/v1/intake/${conversationId}/prep-note`)
}

/** Submit the patient-reviewed prep note (optional edits) to the provider. */
export function submitPrepNote(conversationId: string, edits: PrepNoteSubmit = {}): Promise<PrepNote> {
  return postJson<PrepNote>(`/v1/intake/${conversationId}/prep-note/submit`, edits)
}
